import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DocAbilities } from '@pm/shared';
import { api } from '@/lib/api';
import { homeBody, homeLists, keys, type Home } from '@/lib/query';

type Person = { id: string; name: string; avatarUrl: string | null };

export interface DocListItem {
  id: string;
  /** Null for an organization's own doc. */
  workspaceId: string | null;
  organizationId: string | null;
  folderId: string | null;
  name: string;
  isPrivate: boolean;
  createdById: string;
  createdBy: Person;
  updatedAt: string;
}

export interface DocView extends DocListItem {
  /** Sanitized HTML. */
  content: string | null;
  /** What the person looking may do here - decided by the API, mirrored by the page. */
  abilities: DocAbilities;
}

export interface DocComment {
  id: string;
  parentId: string | null;
  quote: string | null;
  content: string;
  /** Ids of the people the comment mentions, as with task updates. */
  mentions: string[] | null;
  createdAt: string;
  user: Person;
  canDelete: boolean;
}

export interface DocThread extends DocComment {
  resolvedAt: string | null;
  resolvedBy: Person | null;
  replies: DocComment[];
}

export const useDocs = (workspaceId: string) =>
  useQuery({
    queryKey: keys.docs(workspaceId),
    queryFn: () => api.get<DocListItem[]>(`/workspaces/${workspaceId}/docs`),
    enabled: Boolean(workspaceId),
  });

/** An organization's own docs, each with what the person looking may do to it. */
export const useOrganizationDocs = (organizationId: string) =>
  useQuery({
    queryKey: keys.organizationDocs(organizationId),
    queryFn: () => api.get<(DocListItem & { abilities: DocAbilities })[]>(`/organizations/${organizationId}/docs`),
    enabled: Boolean(organizationId),
  });

/**
 * One doc with its body. Always fetched fresh on opening: the editor reads
 * the body once, so a cached copy from an earlier visit would be what it
 * edits and saves back.
 */
export const useDoc = (docId: string) =>
  useQuery({
    queryKey: keys.doc(docId),
    queryFn: () => api.get<DocView>(`/docs/${docId}`),
    enabled: Boolean(docId),
    staleTime: 0,
    refetchOnMount: 'always',
  });

/** Who the @ list offers in this doc - on a private one, only the people who can open it. */
export const useDocMentionable = (docId: string) =>
  useQuery({
    queryKey: ['docs', docId, 'mentionable'],
    queryFn: () => api.get<Person[]>(`/docs/${docId}/mentionable`),
    enabled: Boolean(docId),
    staleTime: 5 * 60_000,
  });

export const useDocMembers = (docId: string, enabled: boolean) =>
  useQuery({
    queryKey: ['docs', docId, 'members'],
    queryFn: () => api.get<Person[]>(`/docs/${docId}/members`),
    enabled: Boolean(docId) && enabled,
  });

export function useCreateDoc(home: Home) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; folderId: string | null; isPrivate: boolean }) =>
      api.post<DocView>('/docs', { ...body, ...homeBody(home) }),
    onSuccess: (doc) => {
      qc.setQueryData(keys.doc(doc.id), doc);
      void qc.invalidateQueries({ queryKey: homeLists(home).docs });
      void qc.invalidateQueries({ queryKey: homeLists(home).folders });
    },
  });
}

/** Everything but the body, which autosave sends - see useDocAutosave. */
export function useDocActions(docId: string, home: Home) {
  const qc = useQueryClient();
  // The workspace or organization page and the side panel list docs, and a
  // private doc can decide whether a folder shows at all, so both refresh.
  const refreshLists = () => {
    void qc.invalidateQueries({ queryKey: homeLists(home).docs });
    void qc.invalidateQueries({ queryKey: homeLists(home).folders });
  };
  const keepView = (doc: DocListItem & { abilities: DocAbilities }) => {
    qc.setQueryData<DocView>(keys.doc(docId), (old) => (old ? { ...old, ...doc } : old));
    refreshLists();
  };

  return {
    update: useMutation({
      mutationFn: (body: { isPrivate?: boolean; folderId?: string | null }) =>
        api.put<DocListItem & { abilities: DocAbilities }>(`/docs/${docId}`, body),
      onSuccess: (doc) => {
        keepView(doc);
        void qc.invalidateQueries({ queryKey: ['docs', docId, 'mentionable'] });
      },
    }),
    remove: useMutation({
      mutationFn: () => api.delete(`/docs/${docId}`),
      onSuccess: refreshLists,
    }),
    setMembers: useMutation({
      mutationFn: (userIds: string[]) => api.put<Person[]>(`/docs/${docId}/members`, { userIds }),
      onSuccess: (members) => {
        qc.setQueryData(['docs', docId, 'members'], members);
        void qc.invalidateQueries({ queryKey: ['docs', docId, 'mentionable'] });
      },
    }),
    transfer: useMutation({
      mutationFn: (userId: string) => api.post<DocView>(`/docs/${docId}/transfer`, { userId }),
      onSuccess: (doc) => {
        keepView(doc);
        void qc.invalidateQueries({ queryKey: ['docs', docId, 'members'] });
      },
    }),
    createItems: useMutation({
      mutationFn: (body: { boardId: string; groupId: string; lines: string[] }) =>
        api.post<{ created: number; boardId: string }>(`/docs/${docId}/items`, { groupId: body.groupId, lines: body.lines }),
      onSuccess: ({ boardId }) => qc.invalidateQueries({ queryKey: keys.board(boardId) }),
    }),
  };
}

/** Moving a doc between folders, from the workspace or organization page. */
export function useMoveDoc(home: Home) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ docId, folderId }: { docId: string; folderId: string | null }) =>
      api.put(`/docs/${docId}`, { folderId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: homeLists(home).docs });
      void qc.invalidateQueries({ queryKey: homeLists(home).folders });
    },
  });
}

/** A board's groups, for picking where items made from a doc go. */
export const useBoardGroups = (boardId: string) =>
  useQuery({
    queryKey: ['boards', boardId, 'groups'],
    queryFn: () => api.get<{ id: string; name: string }[]>(`/boards/${boardId}/groups`),
    enabled: Boolean(boardId),
  });

export const useDocComments = (docId: string) =>
  useQuery({
    queryKey: keys.docComments(docId),
    queryFn: () => api.get<DocThread[]>(`/docs/${docId}/comments`),
    enabled: Boolean(docId),
  });

export function useDocCommentActions(docId: string) {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: keys.docComments(docId) });

  return {
    post: useMutation({
      mutationFn: (body: { content: string; quote?: string | null; parentId?: string; mentions: string[] }) =>
        api.post<{ id: string }>(`/docs/${docId}/comments`, body),
      onSuccess: refresh,
    }),
    resolve: useMutation({
      mutationFn: ({ id, resolved }: { id: string; resolved: boolean }) =>
        api.put(`/doc-comments/${id}`, { resolved }),
      onSuccess: refresh,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api.delete(`/doc-comments/${id}`),
      onSuccess: refresh,
    }),
  };
}
